import React from 'react';
import { AuthContext } from 'contexts';
import styled from 'styled-components';
import { ColumnsType } from 'antd/es/table';
import { useBreakpoint } from 'hooks';
import {
  InputTextArea,
  Icon,
  Select,
  Input,
  InputNumber,
  RadioGroup,
  RadioButton,
  Button,
  Table,
  Card,
} from 'components';

import { RadioChangeEvent } from 'antd';
import { CreateListingCardProps, RoomsFields } from '../types';
import { StyledGrid, StyledTableCell } from './StyledComponents';

type RoomsProps = CreateListingCardProps<RoomsFields | 'roomUnit'>;

type Room = RoomsProps['rooms'][number];

const Rooms: React.FC<RoomsProps> = ({ rooms, roomUnit, setListing }) => {
  const {
    auth: {
      org: { country },
    },
  } = React.useContext(AuthContext);
  const { md } = useBreakpoint();
  const [unit, setUnit] = React.useState<'m' | 'ft'>(
    country === 'USA' ? 'ft' : roomUnit || 'm',
  );

  const convert = (input: React.ReactText, targetUnit: 'm' | 'ft') => {
    if (!input && input !== 0) return null;

    const factor = targetUnit === 'm' ? 0.3048 : 3.28084;
    const num = Number(input);
    if (Number.isNaN(num)) return input.toString();
    return (num * factor).toString();
  };

  const unitChangeHandler = (e: RadioChangeEvent) => {
    const updateValue = e.target.value;
    if (country === 'CAN') {
      setListing(prev => ({
        ...prev,
        roomUnit: updateValue,
      }));
    }
    setUnit(updateValue);
  };

  const getDisplayValue = (input: number | string) => {
    if (!input && input !== 0) return null;

    let displayValue = input;
    if (unit === 'ft') displayValue = convert(displayValue, 'ft');
    return Number(displayValue);
  };

  const addRoom = () => {
    setListing(prev => ({
      ...prev,
      rooms: [
        ...rooms,
        {
          id: rooms.length + 1,
          level: null,
          room: null,
          length: null,
          width: null,
          description_1: null,
          description_2: null,
          description_3: null,
        },
      ],
    }));
  };

  const editRoom = (changes: Partial<Room>, i: number) => {
    const newRooms = [...rooms];
    newRooms[i] = { ...newRooms[i], ...changes };
    setListing(prev => ({
      ...prev,
      rooms: newRooms,
    }));
  };

  const deleteRoom = (i: number) => {
    setListing(prev => ({
      ...prev,
      rooms: rooms.filter((r, idx) => idx !== i),
    }));
  };

  const columns: ColumnsType<Room> = [
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 150,
      render: (value: Room['level'], { id }, i) => (
        <Select
          allowClear
          value={value}
          onChange={newValue => editRoom({ level: newValue }, i)}
          options={[
            { label: '2nd', value: '2nd' },
            { label: '3rd', value: '3rd' },
            { label: 'Bsmt', value: 'Bsmt' },
            { label: 'Flat', value: 'Flat' },
            { label: 'Ground', value: 'Ground' },
            { label: 'In Betwn', value: 'In Betwn' },
            { label: 'Lower', value: 'Lower' },
            { label: 'Main', value: 'Main' },
            { label: 'Sub-Bsmt', value: 'Sub-Bsmt' },
            { label: 'Upper', value: 'Upper' },
          ]}
          data-cy={`listing-room-level-${id}`}
        />
      ),
    },
    {
      title: 'Room',
      dataIndex: 'room',
      key: 'room',
      width: 200,
      render: (value: Room['room'], { id }, i) => (
        <StyledTableCell $minWidth={200}>
          <Input
            value={value}
            onChange={({ target: { value: newValue } }) =>
              editRoom({ room: newValue }, i)
            }
            data-cy={`listing-room-name-${id}`}
          />
        </StyledTableCell>
      ),
    },
    {
      title: `Length (${unit})`,
      dataIndex: 'length',
      key: 'length',
      width: 100,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: Room['length'], { id }, i) => (
        <InputNumber
          hideArrows
          precision={2}
          min={0.0}
          step={0.1}
          value={getDisplayValue(value)}
          onChange={(newValue: string) =>
            editRoom(
              { length: unit === 'm' ? newValue : convert(newValue, 'm') },
              i,
            )
          }
          data-cy={`listing-room-length-${id}`}
        />
      ),
    },
    {
      title: `Width (${unit})`,
      dataIndex: 'width',
      key: 'width',
      width: 100,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: Room['width'], { id }, i) => (
        <InputNumber
          hideArrows
          precision={2}
          min={0.0}
          step={0.1}
          value={getDisplayValue(value)}
          onChange={(newValue: string) =>
            editRoom(
              { width: unit === 'm' ? newValue : convert(newValue, 'm') },
              i,
            )
          }
          data-cy={`listing-room-width-${id}`}
        />
      ),
    },
    {
      title: `Description 1`,
      dataIndex: 'description_1',
      key: 'description_1',
      width: 200,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: Room['description_1'], { id }, i) => (
        <StyledTableCell $minWidth={200}>
          <InputTextArea
            value={value}
            autoSize
            onChange={({ target: { value: newValue } }) =>
              editRoom({ description_1: newValue }, i)
            }
            data-cy={`listing-room-description1-${id}`}
          />
        </StyledTableCell>
      ),
    },
    {
      title: `Description 2`,
      dataIndex: 'description_2',
      key: 'description_2',
      width: 200,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: Room['description_2'], { id }, i) => (
        <StyledTableCell $minWidth={200}>
          <InputTextArea
            value={value}
            autoSize
            onChange={({ target: { value: newValue } }) =>
              editRoom({ description_2: newValue }, i)
            }
            data-cy={`listing-room-description2-${id}`}
          />
        </StyledTableCell>
      ),
    },
    {
      title: `Description 3`,
      dataIndex: 'description_3',
      key: 'description_3',
      width: 200,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: Room['description_3'], { id }, i) => (
        <StyledTableCell $minWidth={200}>
          <InputTextArea
            value={value}
            autoSize
            onChange={({ target: { value: newValue } }) =>
              editRoom({ description_3: newValue }, i)
            }
            data-cy={`listing-room-description3-${id}`}
          />
        </StyledTableCell>
      ),
    },
    {
      title: '',
      dataIndex: '',
      key: 'delete',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (value, record, i) => (
        <Button size="small" onClick={() => deleteRoom(i)}>
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Card
      type="primary"
      title="Rooms"
      id="rooms"
      subtitle={
        <StyledSubtitle>
          <RadioGroup
            value={unit}
            size="small"
            buttonStyle="solid"
            onChange={unitChangeHandler}
          >
            <RadioButton value="m" data-cy="listing-unit-choice-meters">
              Meters
            </RadioButton>
            <RadioButton value="ft" data-cy="listing-unit-choice-feet">
              Feet
            </RadioButton>
          </RadioGroup>
        </StyledSubtitle>
      }
      extra={
        <Button
          type="default"
          icon={<Icon type="plus" height={16} offset={[1, 0]} />}
          onClick={addRoom}
          data-cy="listing-add-room-button"
        >
          {md && 'Add Room'}
        </Button>
      }
    >
      <StyledGrid $columns={1}>
        <StyledTableWrapper>
          <Table
            size="small"
            dataSource={rooms.map(room => ({ ...room, key: room.id }))}
            columns={columns}
            pagination={false}
            style={{ overflow: 'auto' }}
            scroll={{
              x: 'auto',
            }}
          />
        </StyledTableWrapper>
      </StyledGrid>
    </Card>
  );
};

const StyledSubtitle = styled.span`
  margin-left: 25px;
`;

const StyledTableWrapper = styled.div`
  grid-column-end: span 2;
`;

export default React.memo(Rooms);
